import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, finalize, of } from 'rxjs';

import { FormDateFieldComponent, FormValidationErrorsComponent } from '@components/form-fields';
import { SpinnerComponent } from '@components/spinner';
import { SnackBar } from '@components/snack-bar';
import { AuthService } from '@features/auth';
import { ApiErrorResponse, ageValidator } from '@features/common';

@Component({
  selector: 'app-birthdate-modal',
  imports: [CommonModule, ReactiveFormsModule, FormDateFieldComponent, FormValidationErrorsComponent, SpinnerComponent],
  templateUrl: './birthdate-modal.component.html',
  styleUrl: './birthdate-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class BirthdateModalComponent {
  #authService = inject(AuthService);
  #snackBar = inject(SnackBar);
  #destroyRef = inject(DestroyRef);

  protected birthDateControl = new FormControl('', [Validators.required, ageValidator()]);
  protected form = new FormGroup({ birth_date: this.birthDateControl });
  protected isBusy = signal<boolean>(false);

  protected maxBirthDate = (() => {
    const today = new Date();
    const year = today.getFullYear() - 18;
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  })();

  onSubmit(): void {
    this.birthDateControl.markAsTouched();

    if (this.birthDateControl.valid && this.birthDateControl.value) {
      this.isBusy.set(true);

      this.#authService
        .updateBirthdate(this.birthDateControl.value)
        .pipe(
          takeUntilDestroyed(this.#destroyRef),
          catchError((response: ApiErrorResponse) => {
            if (response?.errors?.['birth_date']) {
              this.birthDateControl.setErrors({ serverError: response.errors['birth_date'][0] });
            }
            this.#snackBar.error(response?.message ?? 'Failed to save your date of birth. Please try again.');
            return of(null);
          }),
          finalize(() => this.isBusy.set(false)),
        )
        .subscribe();
    }
  }
}
